import { z } from 'zod'
import {
  COMMON_CLIENT_MESSAGES,
  type CommonServerMessage,
  type ErrorCode,
  type PlayerPublic,
} from '../protocol'
import type { Dir, Pos, SlideOutcome } from './types'

export const MAX_RACERS = 8
/** The host can't start a race alone — need someone to actually race against. */
export const MIN_RACERS = 2

export const dirSchema = z.enum(['up', 'down', 'left', 'right'])

export const iceClientMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('takeSeat') }),
  z.object({ type: z.literal('start'), levelId: z.string().min(1).max(64) }),
  z.object({ type: z.literal('move'), dir: dirSchema }),
  z.object({ type: z.literal('next') }),
  z.object({ type: z.literal('leave') }),
  ...COMMON_CLIENT_MESSAGES,
])
export type IceClientMessage = z.infer<typeof iceClientMessageSchema>

export type IcePhase = 'lobby' | 'countdown' | 'racing' | 'results'

export interface IceRacer {
  profile: PlayerPublic
  pos: Pos
  moves: number
  cracked: number[]
  hasKey: boolean
  falls: number
  finishedAt: number | null
  place: number | null
  online: boolean
}

export interface IceSnapshot {
  code: string
  seq: number
  phase: IcePhase
  hostId: string
  levelId: string | null
  round: number
  racers: IceRacer[]
  scores: Record<string, number>
  /** Countdown end during `countdown`, race end during `racing`, else null. */
  deadlineAt: number | null
  countdownMs: number
  raceMs: number
  spectators: number
  lastResults: { levelId: string; placeById: Record<string, number> } | null
}

export const ICE_ERROR_CODES = [
  'NOT_HOST',
  'NOT_RACING',
  'NOT_RACER',
  'ALREADY_FINISHED',
  'ROOM_FULL',
  'BAD_LEVEL',
  'BAD_PHASE',
  'NEED_MORE_RACERS',
] as const
export type IceErrorCode = ErrorCode | (typeof ICE_ERROR_CODES)[number]

export const ICE_ERROR_TEXT: Record<(typeof ICE_ERROR_CODES)[number], string> = {
  NOT_HOST: 'Only the host can do that',
  NOT_RACING: 'The race has not started',
  NOT_RACER: 'You are watching this race',
  ALREADY_FINISHED: 'You already reached the door',
  ROOM_FULL: `This race is full (${MAX_RACERS} players)`,
  BAD_LEVEL: 'Unknown level',
  BAD_PHASE: 'Not possible right now',
  NEED_MORE_RACERS: 'Wait for at least one more racer to join',
}

export interface MovedEvent {
  playerId: string
  dir: Dir
  path: Pos[]
  end: Pos
  outcome: SlideOutcome
  moves: number
  gotKey?: boolean
}

export type IceServerMessage =
  | CommonServerMessage<IceErrorCode>
  | { type: 'state'; snapshot: IceSnapshot }
  | ({ type: 'moved' } & MovedEvent)

export interface IceRoomSummary {
  code: string
  phase: IcePhase
  racers: number
  hostNickname: string | null
}

/** Placement points: 1st gets N (racer count), 2nd N-1, …; unfinished racers get 0. */
export function pointsFor(place: number | null, racerCount: number): number {
  if (place === null) return 0
  return Math.max(0, racerCount - place + 1)
}
